import { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { EIpoStateType } from 'stockbitipo/eipo-order';
import useSyariah from 'global/TradingModal/hooks/useSyariah';

import ERROR_MESSAGE from 'constants/errorMessage';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import { Modal } from '../../utils/styled';
import ModalSuccessInfo from '../modal-success';

import {
  KEY_EVENT_CLOSE_MODAL_BUY,
  KEY_EVENT_CLOSE_MODAL_SUCCESS,
  KEY_EVENT_OPEN_MODAL_BUY,
  KEY_EVENT_OPEN_MODAL_DEPOSIT,
  KEY_EVENT_OPEN_MODAL_SUCCESS,
  KEY_EVENT_SHOW_TOAST_ERROR,
} from '../../utils/constants';

const IpoOrder = dynamic(
  // @ts-ignore
  // eslint-disable-next-line
  () => import('stockbitipo/eipo-order'),
  {
    ssr: false,
  },
);

type IEIpoOrder = {
  emiten: any;
};

type ModalInfoType = {
  emitenCode: string | null;
  state: EIpoStateType | null;
  isOpen: boolean;
};

const EIpoOrder = ({ emiten }: IEIpoOrder) => {
  const { isUserSyariah, showSyariahRestrictionModal } = useSyariah();

  const [modalSuccess, setModalSuccess] = useState({
    isOpen: false,
    content: '',
  });

  const [modalInfo, setModalInfo] = useState<ModalInfoType>({
    emitenCode: null,
    state: null,
    isOpen: false,
  });

  const onResetModalState = () => {
    const event = new CustomEvent('handleSetModalContent');
    window.dispatchEvent(event);
  };

  const listenOpenBuyModal = useCallback(
    async (event: CustomEvent) => {
      onResetModalState();

      if (event.detail) {
        // Show error modal if user is syariah and stock is not syariah tradable
        if (isUserSyariah && !event.detail.isSharia) {
          showSyariahRestrictionModal();
          return;
        }

        setModalInfo({
          emitenCode: event.detail.emiten,
          state: event.detail.state,
          isOpen: true,
        });
      }
    },
    [isUserSyariah],
  );

  const listenCloseBuyModal = () => {
    setModalInfo({
      emitenCode: null,
      state: null,
      isOpen: false,
    });
  };

  const closeBuyModal = () => {
    setModalInfo({
      emitenCode: null,
      state: null,
      isOpen: false,
    });
    onResetModalState();
  };

  const listenOpenSuccessModal = (event: CustomEvent) => {
    if (event.detail) {
      setModalSuccess({
        isOpen: true,
        content: event.detail,
      });
    }
  };

  const closeSuccessModal = () => {
    const event = new CustomEvent(KEY_EVENT_CLOSE_MODAL_SUCCESS);
    window.dispatchEvent(event);
    setModalSuccess({
      isOpen: false,
      content: '',
    });
  };

  const listenOpenModalDeposit = () => {
    if (typeof window !== 'undefined') {
      window.open('/securities/deposit/', '_blank');
    }
  };

  const listenToastError = (event: CustomEvent) => {
    if (event.detail) {
      handleErrorMessageAPI(event.detail.message, ERROR_MESSAGE.ALERT_RED);
    }
  };

  useEffect(() => {
    window.addEventListener(
      KEY_EVENT_OPEN_MODAL_BUY,
      listenOpenBuyModal as EventListener,
    );
    window.addEventListener(
      KEY_EVENT_CLOSE_MODAL_BUY,
      listenCloseBuyModal as EventListener,
    );
    window.addEventListener(
      KEY_EVENT_OPEN_MODAL_SUCCESS,
      listenOpenSuccessModal as EventListener,
    );
    window.addEventListener(
      KEY_EVENT_OPEN_MODAL_DEPOSIT,
      listenOpenModalDeposit as EventListener,
    );
    window.addEventListener(
      KEY_EVENT_SHOW_TOAST_ERROR,
      listenToastError as EventListener,
    );

    return () => {
      window.removeEventListener(
        KEY_EVENT_OPEN_MODAL_BUY,
        listenOpenBuyModal as EventListener,
      );
      window.removeEventListener(
        KEY_EVENT_CLOSE_MODAL_BUY,
        listenCloseBuyModal as EventListener,
      );
      window.removeEventListener(
        KEY_EVENT_OPEN_MODAL_SUCCESS,
        listenOpenSuccessModal as EventListener,
      );
      window.removeEventListener(
        KEY_EVENT_OPEN_MODAL_DEPOSIT,
        listenOpenModalDeposit as EventListener,
      );
      window.removeEventListener(
        KEY_EVENT_SHOW_TOAST_ERROR,
        listenToastError as EventListener,
      );
    };
  }, [listenOpenBuyModal]);

  const showContent = modalInfo.isOpen && modalInfo.emitenCode !== null;

  return (
    <>
      <Modal
        visible={showContent}
        onCancel={closeBuyModal}
        closable={false}
        width={480}
        destroyOnClose
      >
        <IpoOrder
          emitenCode={modalInfo.emitenCode}
          state={modalInfo.state}
          isDark={false}
        />
      </Modal>
      <ModalSuccessInfo
        visible={modalSuccess.isOpen}
        onCancel={closeSuccessModal}
        onConfirm={closeSuccessModal}
        infoText={modalSuccess.content}
      />
    </>
  );
};

export default EIpoOrder;
